import { useState } from 'react';
import { getSelectedAccountInfo } from '@ibk/shared-auth';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { createSha256 } from '@ibk/shared-utils';
import { ApiErrors } from '../../enums';
import { usePostTransferPixMutation, usePostTransferPixV3Mutation } from '../../services/pixApi';
import { handlePixError } from '../../utils/pixErrorHandler';
const ERRORS_MESSAGES = {
    [ApiErrors.CAN_NOT_TRANSFER_PIX]: 'transaction-not-done-try-again-later',
    [ApiErrors.NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.ERROR_NO_FUNDS_AVAILABLE]: 'insufficient-balance-modal-title',
    [ApiErrors.PIN_FAILURE_TO_VALIDATE]: 'pin-failure-to-validate-error',
    [ApiErrors.TIMEOUT_ERROR]: 'pix-timeout-error',
    [ApiErrors.PIX_LIMIT_EXCEEDED]: 'pix-limit-exceeded',
    [ApiErrors.PAYMENT_ALREADY_EXISTS]: 'payment-in-processing',
};
export function usePostTransferPix() {
    const { t } = useTranslation();
    const { isFeatureFlagWhiteListed, cache } = useFeatureFlag(undefined, {
        autoLoad: false,
    });
    const [triggerV2] = usePostTransferPixMutation();
    const [triggerV3] = usePostTransferPixV3Mutation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [pix, setPix] = useState();
    async function postTransferPix(body, startAt, rethrow) {
        setLoading(true);
        setError(undefined);
        setPix(undefined);
        try {
            const usePermissions = isFeatureFlagWhiteListed(FeatureFlagUseCase.PAYMENTS_OPERATOR_REQUEST_PAYMENT, undefined, cache?.data);
            const isCoreBankingBaasV2Enabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.CORE_BANKING_ENABLE_BAAS_V2);
            const trigger = isCoreBankingBaasV2Enabled ? triggerV3 : triggerV2;
            const selectedAccountId = getSelectedAccountInfo()?.accountId;
            const transactionIdempotencyId = await createSha256({
                transactionAt: startAt,
                accountId: selectedAccountId,
                amount: body.amount,
                key: body?.pixDetails?.key,
                pin: body.credential,
                accountPayablesId: body.accountPayablesId,
            });
            const { result } = await trigger({
                body,
                transactionIdempotencyId,
                usePermissions,
            }).unwrap();
            setPix(result);
            return result;
        }
        catch (err) {
            const errorMessage = handlePixError({
                error: err,
                translate: t,
                errorMessages: ERRORS_MESSAGES,
                defaultErrorKey: ApiErrors.CAN_NOT_TRANSFER_PIX,
            });
            setError(errorMessage);
            if (rethrow) {
                throw new Error(errorMessage);
            }
        }
        finally {
            setLoading(false);
        }
    }
    return {
        loading,
        setLoading,
        error,
        setError,
        pix,
        setPix,
        postTransferPix,
    };
}
